import React, { useState } from 'react';
import { Moment, UserProfile } from '../types';
import { Heart, MapPin, Plus, Sparkles, Calendar, Share2, MessageCircle } from 'lucide-react';
import { soundEffects } from '../lib/soundEffects';

interface MomentsViewProps {
  currentUser: UserProfile;
  moments: Moment[];
  onOpenCreateMoment: () => void;
  onLikeMoment: (momentId: string) => void;
}

export const MomentsView: React.FC<MomentsViewProps> = ({
  currentUser,
  moments,
  onOpenCreateMoment,
  onLikeMoment,
}) => {
  const [likedMap, setLikedMap] = useState<Record<string, boolean>>({});

  const handleLike = (id: string) => {
    setLikedMap((prev) => ({ ...prev, [id]: !prev[id] }));
    onLikeMoment(id);
    soundEffects.playTap();
  };

  return (
    <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-[#060911] font-['Cairo']">
      {/* Top Banner */}
      <div className="max-w-2xl mx-auto mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-black text-white flex items-center gap-2">
            <span>لحظات وتين</span>
            <Sparkles className="w-5 h-5 text-emerald-400" />
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">
            سجل ذكرياتك ولحظاتك المميزة وشاركها مع دائرة وتين المقربة
          </p>
        </div>

        <button
          onClick={onOpenCreateMoment}
          className="px-4 py-2 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white font-bold text-xs shadow-lg shadow-emerald-500/20 flex items-center gap-1.5 transition active:scale-95"
        >
          <Plus className="w-4 h-4" />
          <span>لحظة جديدة</span>
        </button>
      </div>

      {/* Moments Feed */}
      <div className="max-w-2xl mx-auto space-y-6">
        {moments.map((mom) => {
          const isLiked = likedMap[mom.id] || mom.hasLiked;

          return (
            <div
              key={mom.id}
              className="bg-[#0e1422] border border-slate-800/80 rounded-3xl overflow-hidden shadow-xl"
            >
              {/* Moment Header */}
              <div className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <img
                    src={mom.userAvatar}
                    alt={mom.displayName}
                    className="w-10 h-10 rounded-2xl object-cover ring-1 ring-emerald-500/40"
                  />
                  <div>
                    <div className="text-xs font-bold text-white flex items-center gap-1">
                      <span>{mom.displayName}</span>
                      <span className="text-[10px] text-slate-400 font-mono">@{mom.username}</span>
                    </div>
                    <div className="flex items-center gap-2 text-[10px] text-slate-400 mt-0.5">
                      <span className="flex items-center gap-0.5">
                        <MapPin className="w-3 h-3 text-emerald-400" />
                        {mom.location}
                      </span>
                      <span>•</span>
                      <span>{mom.dateStr}</span>
                    </div>
                  </div>
                </div>

                <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-300 font-semibold border border-emerald-500/20">
                  {mom.mood === 'happy' ? '😄 فرح' : mom.mood === 'calm' ? '🌿 روقان' : '✨ مميز'}
                </span>
              </div>

              {/* Photo */}
              <div className="relative aspect-video w-full bg-slate-900 overflow-hidden">
                <img
                  src={mom.imageUrl}
                  alt={mom.title}
                  className="w-full h-full object-cover hover:scale-105 transition duration-500"
                />
              </div>

              {/* Moment Footer */}
              <div className="p-4">
                <h3 className="text-sm font-bold text-white mb-2">{mom.title}</h3>

                <div className="flex items-center justify-between pt-2 border-t border-slate-800/60 text-slate-400 text-xs">
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => handleLike(mom.id)}
                      className={`flex items-center gap-1.5 transition ${
                        isLiked ? 'text-rose-500 font-bold' : 'hover:text-rose-400'
                      }`}
                    >
                      <Heart className={`w-4 h-4 ${isLiked ? 'fill-current' : ''}`} />
                      <span>{mom.likesCount + (isLiked ? 1 : 0)} إعجاب</span>
                    </button>

                    <button className="flex items-center gap-1.5 hover:text-white transition">
                      <MessageCircle className="w-4 h-4" />
                      <span>تعليق</span>
                    </button>
                  </div>

                  <button className="p-1 hover:text-white transition">
                    <Share2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
